import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AccordionText } from "@faire/retailer-visitor-shared/views/product/ProductDescriptionAccordions/__internal__/AccordionText";
import { ChevronIcon } from "@faire/retailer-visitor-shared/views/product/ProductDescriptionAccordions/__internal__/ChevronIcon";
import { CustomizedCollapsableComponent } from "@faire/retailer-visitor-shared/views/product/ProductDescriptionAccordions/__internal__/CustomizedCollapsableComponent";
import useToggleAccordion from "@faire/retailer-visitor-shared/views/product/ProductDescriptionAccordions/__internal__/useToggleAccordion";
import { getLineHeight, TRANSITION_DURATION } from "@faire/retailer-visitor-shared/views/product/ProductDescriptionAccordions/__internal__/utils";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import delay from "lodash/delay";
import some from "lodash/some";
import sum from "lodash/sum";
import React, { useEffect, useLayoutEffect, useRef } from "react";
import styled from "styled-components";
var BoldFormatting = function(chunks) {
    return /*#__PURE__*/ _jsx(Typography, {
        variant: "paragraphSansMedium",
        as: "span",
        children: chunks
    });
};
export var ListAccordionContainer = function(param) {
    var title = param.title, maxNumberOfDetailsShownWhenCollapsed = param.maxNumberOfDetailsShownWhenCollapsed, maxNumberOfLinesShownPerDetail = param.maxNumberOfLinesShownPerDetail, productDetails = param.productDetails, onExpandAccordionItem = param.onExpandAccordionItem, onView = param.onView;
    var isMobile = useViewport().isMobile;
    var viewRef = useDoActionWhenVisible(onView);
    var _React_useState = _sliced_to_array(React.useState(0), 2), heightOfAllDetailsWhenExpanded = _React_useState[0], setHeightOfAllDetailsWhenExpanded = _React_useState[1];
    var _React_useState1 = _sliced_to_array(React.useState(0), 2), heightOfAllDetailsWhenCollapsed = _React_useState1[0], setHeightOfAllDetailsWhenCollapsed = _React_useState1[1];
    var maxHeightPerDetailWhenCollapsed = maxNumberOfLinesShownPerDetail * getLineHeight();
    // Refs to track the height of each product detail
    var accordionContentRefs = useRef([]);
    var isTruncatedWhenCollapsed = React.useCallback(function(ref) {
        if (!ref) {
            return false;
        }
        return ref.scrollHeight > maxHeightPerDetailWhenCollapsed;
    }, [
        maxHeightPerDetailWhenCollapsed
    ]);
    var getCollapsedHeight = React.useCallback(function(ref) {
        if (!ref) {
            return maxHeightPerDetailWhenCollapsed;
        }
        return isTruncatedWhenCollapsed(ref) ? maxHeightPerDetailWhenCollapsed : ref.scrollHeight;
    }, [
        maxHeightPerDetailWhenCollapsed,
        isTruncatedWhenCollapsed
    ]);
    var isAnyRefTruncatedWhenCollapsed = some(accordionContentRefs.current, function(ref) {
        return ref && isTruncatedWhenCollapsed(ref);
    });
    var hasMoreDetailsThanMaxNumberOfDetailsShownWhenCollapsed = productDetails && productDetails.length > maxNumberOfDetailsShownWhenCollapsed;
    var hasExpandableContent = hasMoreDetailsThanMaxNumberOfDetailsShownWhenCollapsed || isAnyRefTruncatedWhenCollapsed;
    var _React_useState2 = _sliced_to_array(React.useState(true), 2), collapseTransitionEnd = _React_useState2[0], setCollapseTransitionEnd = _React_useState2[1];
    var _useToggleAccordion = useToggleAccordion({
        hasExpandableContent: hasExpandableContent,
        onExpandAccordionItem: onExpandAccordionItem
    }), isAccordionOpen = _useToggleAccordion.isAccordionOpen, toggleIsAccordionOpen = _useToggleAccordion.toggleIsAccordionOpen;
    // Delays the line clamp / hiding of text to be applied only when the collapse transition ends
    useEffect(function() {
        if (isAccordionOpen) {
            setCollapseTransitionEnd(false);
            return;
        }
        var timeout = delay(function() {
            setCollapseTransitionEnd(true);
        }, TRANSITION_DURATION);
        return function() {
            return clearTimeout(timeout);
        };
    }, [
        isAccordionOpen
    ]);
    var getHeightOfAllDetailsWhenCollapsed = React.useCallback(function() {
        return sum(accordionContentRefs.current.slice(0, maxNumberOfDetailsShownWhenCollapsed).map(function(ref) {
            return getCollapsedHeight(ref);
        }));
    }, [
        getCollapsedHeight,
        maxNumberOfDetailsShownWhenCollapsed
    ]);
    var getHeightOfAllDetailsWhenExpanded = React.useCallback(function() {
        return sum(accordionContentRefs.current.map(function(ref) {
            var _ref_scrollHeight;
            return (_ref_scrollHeight = ref === null || ref === void 0 ? void 0 : ref.scrollHeight) !== null && _ref_scrollHeight !== void 0 ? _ref_scrollHeight : maxHeightPerDetailWhenCollapsed;
        }));
    }, [
        maxHeightPerDetailWhenCollapsed
    ]);
    var updateHeights = React.useCallback(function() {
        setHeightOfAllDetailsWhenExpanded(getHeightOfAllDetailsWhenExpanded());
        setHeightOfAllDetailsWhenCollapsed(getHeightOfAllDetailsWhenCollapsed());
    }, [
        getHeightOfAllDetailsWhenCollapsed,
        getHeightOfAllDetailsWhenExpanded
    ]);
    useLayoutEffect(function() {
        var _productDetails_length;
        accordionContentRefs.current = accordionContentRefs.current.slice(0, (_productDetails_length = productDetails === null || productDetails === void 0 ? void 0 : productDetails.length) !== null && _productDetails_length !== void 0 ? _productDetails_length : 0);
        updateHeights();
    }, [
        getHeightOfAllDetailsWhenExpanded,
        getHeightOfAllDetailsWhenCollapsed,
        maxHeightPerDetailWhenCollapsed,
        productDetails,
        updateHeights
    ]);
    useEffect(function() {
        var _getWindow;
        var handleResize = debounceIfPossible(function() {
            updateHeights();
        }, 200);
        (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.addEventListener("resize", handleResize);
        handleResize();
        return function() {
            var _getWindow;
            (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.removeEventListener("resize", handleResize);
        };
    }, [
        updateHeights
    ]);
    var shouldShowEllipsis = isMobile && hasExpandableContent && !isAccordionOpen;
    var isListItemFullTextVisible = function(listIndex) {
        return isAccordionOpen || !collapseTransitionEnd || !isTruncatedWhenCollapsed(accordionContentRefs.current[listIndex]);
    };
    var isListItemVisible = function(listIndex) {
        return isAccordionOpen || !collapseTransitionEnd || listIndex < maxNumberOfDetailsShownWhenCollapsed;
    };
    var formatProductDetailEntry = function(detail) {
        var cleanText = function(text) {
            var _text_replace;
            return (_text_replace = text === null || text === void 0 ? void 0 : text.replace(/^[•]\s*/, "")) !== null && _text_replace !== void 0 ? _text_replace : null;
        }; // Remove any starting bullet points from the text
        if (detail.name && detail.value) {
            return /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "f7kOkk",
                defaultMessage: [
                    {
                        type: 8,
                        value: "BoldFormatting",
                        children: [
                            {
                                type: 1,
                                value: "DetailNameFullyTranslatedSentence"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: ": "
                    },
                    {
                        type: 1,
                        value: "DetailValueFullyTranslatedSentence"
                    }
                ],
                values: {
                    BoldFormatting: BoldFormatting,
                    DetailNameFullyTranslatedSentence: detail.name,
                    DetailValueFullyTranslatedSentence: detail.value
                }
            });
        }
        var _detail_value, _ref, _ref1;
        return (_ref1 = (_ref = (_detail_value = detail.value) !== null && _detail_value !== void 0 ? _detail_value : cleanText(detail.text)) !== null && _ref !== void 0 ? _ref : detail.name) !== null && _ref1 !== void 0 ? _ref1 : null;
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                size: "2x"
            }),
            isMobile ? /*#__PURE__*/ _jsx(HorizontalLine, {}) : null,
            /*#__PURE__*/ _jsxs(DesktopColumnMobileRow, {
                $hasExpandableContent: hasExpandableContent,
                ref: onView ? viewRef : undefined,
                onClick: isMobile ? toggleIsAccordionOpen : function() {},
                children: [
                    /*#__PURE__*/ _jsxs(Column, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraphSansMedium",
                                children: title
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: "1x"
                            }),
                            /*#__PURE__*/ _jsx(CustomizedCollapsableComponent, {
                                $isAccordionOpen: isAccordionOpen,
                                $componentExpandedHeight: heightOfAllDetailsWhenExpanded,
                                $componentCollapsedHeight: heightOfAllDetailsWhenCollapsed,
                                $isList: true,
                                as: "ul",
                                children: productDetails === null || productDetails === void 0 ? void 0 : productDetails.map(function(detail, index) {
                                    var _accordionContentRefs_current_index;
                                    var _accordionContentRefs_current_index_scrollHeight;
                                    return /*#__PURE__*/ _jsx(ListItem, {
                                        style: {
                                            visibility: isListItemVisible(index) ? "visible" : "hidden"
                                        },
                                        "data-test-id": "accordion-list-item",
                                        children: /*#__PURE__*/ _jsx(AccordionText, {
                                            ref: function(el) {
                                                accordionContentRefs.current[index] = el;
                                            },
                                            $textExpandedHeight: (_accordionContentRefs_current_index_scrollHeight = (_accordionContentRefs_current_index = accordionContentRefs.current[index]) === null || _accordionContentRefs_current_index === void 0 ? void 0 : _accordionContentRefs_current_index.scrollHeight) !== null && _accordionContentRefs_current_index_scrollHeight !== void 0 ? _accordionContentRefs_current_index_scrollHeight : maxHeightPerDetailWhenCollapsed,
                                            $textCollapsedHeight: getCollapsedHeight(accordionContentRefs.current[index]),
                                            $isAccordionOpen: isAccordionOpen,
                                            $isFullTextVisible: isListItemFullTextVisible(index),
                                            $maxNumberOfLinesShownWhenCollapsed: maxNumberOfLinesShownPerDetail,
                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                variant: "paragraphSansRegular",
                                                children: formatProductDetailEntry(detail)
                                            })
                                        })
                                    }, "product-detail-".concat(index));
                                })
                            }),
                            shouldShowEllipsis ? /*#__PURE__*/ _jsx(Ellipsis, {}) : null
                        ]
                    }),
                    hasExpandableContent && /*#__PURE__*/ _jsx(AccordionToggle, {
                        isAccordionOpen: isAccordionOpen,
                        toggleIsAccordionOpen: toggleIsAccordionOpen
                    })
                ]
            })
        ]
    });
};
var Ellipsis = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var ellipsis = strictLocalize({
        id: "81u0qf",
        defaultMessage: [
            {
                type: 0,
                value: "..."
            }
        ]
    });
    return /*#__PURE__*/ _jsx(Typography, {
        variant: "paragraphSansRegular",
        align: "left",
        style: {
            paddingLeft: getSpacing("2x")
        },
        children: ellipsis
    });
};
var AccordionToggle = function(param) {
    var isAccordionOpen = param.isAccordionOpen, toggleIsAccordionOpen = param.toggleIsAccordionOpen;
    var isMobile = useViewport().isMobile;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var accordionHelperText = isAccordionOpen ? strictLocalize({
        id: "uYmeb9",
        defaultMessage: [
            {
                type: 0,
                value: "Fewer details"
            }
        ]
    }) : strictLocalize({
        id: "UEVD/b",
        defaultMessage: [
            {
                type: 0,
                value: "More details"
            }
        ]
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: isMobile ? /*#__PURE__*/ _jsx(ChevronIcon, {
            isAccordionOpen: isAccordionOpen
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Spacer, {
                    size: "1x"
                }),
                /*#__PURE__*/ _jsx(Button, {
                    variant: "tertiary",
                    style: {
                        justifyContent: "flex-start"
                    },
                    onClick: toggleIsAccordionOpen,
                    children: /*#__PURE__*/ _jsxs(Row, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraphSansRegular",
                                children: accordionHelperText
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: "1x"
                            }),
                            /*#__PURE__*/ _jsx(ChevronIcon, {
                                isAccordionOpen: isAccordionOpen
                            })
                        ]
                    })
                })
            ]
        })
    });
};
var DesktopColumnMobileRow = styled(Flex).attrs({
    direction: {
        mobileAndAbove: "row",
        tabletAndAbove: "column"
    },
    align: "flex-start"
}).withConfig({
    componentId: "sc-f1a0d145-0"
})([
    "padding-top:0;",
    "{padding-top:",
    ";cursor:",
    ";}"
], responsiveMediaQueries.mobile, getSpacing("2x"), function(param) {
    var $hasExpandableContent = param.$hasExpandableContent;
    return $hasExpandableContent ? "pointer" : "unset";
});
var ListItem = styled.li.withConfig({
    componentId: "sc-f1a0d145-1"
})([
    "list-style-type:disc;margin-left:",
    ";&::marker{line-height:10px;}"
], getSpacing("2x"));
